import { useEffect, useState } from "react";
import { User, Buildings, Storefront, CalendarBlank, CalendarCheck, Trophy, Target } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import EmptyState from "../components/EmptyState";
import RankBadge, { computeRanks } from "../components/RankBadge";
import FloorTargetBoard from "../components/FloorTargetBoard";
import api from "../lib/api";
import { fmtCurrency, fmtDecimal, hasPerm, scopeActive } from "../lib/format";
import { useAuth } from "../context/AuthContext";

function StatPair({ title, icon: Icon, data, testId, accent }) {
  return (
    <div className={`rounded-lg border p-5 ${accent ? "border-blue-200 bg-blue-50/40" : "border-slate-200 bg-white"}`} data-testid={testId}>
      <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700">
        <Icon size={18} weight="bold" className={accent ? "text-blue-700" : "text-slate-600"} /> {title}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">ABV</div>
          <div className="mt-1 font-display text-2xl font-extrabold leading-none tracking-tight text-slate-900">{fmtCurrency(data?.abv, true)}</div>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">ABQ</div>
          <div className="mt-1 font-display text-2xl font-extrabold leading-none tracking-tight text-slate-900">{fmtDecimal(data?.abq)}</div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, sub, block, hasOwn, showStore }) {
  const cols = (hasOwn ? 2 : 0) + (showStore ? 1 : 0);
  const grid = cols >= 3 ? "sm:grid-cols-2 lg:grid-cols-3" : cols === 2 ? "sm:grid-cols-2" : "sm:grid-cols-1 max-w-sm";
  return (
    <section>
      <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-slate-800">
        <Icon size={20} weight="bold" /> {title} {sub && <span className="text-xs font-normal text-slate-400">{sub}</span>}
      </h2>
      <div className={`grid gap-4 ${grid}`}>
        {hasOwn && <StatPair title="My Performance" icon={User} data={block.own} testId={`staff-${title.toLowerCase().replace(/\s/g, "")}-own`} />}
        {hasOwn && <StatPair title="My Floor" icon={Buildings} data={block.floor} testId={`staff-${title.toLowerCase().replace(/\s/g, "")}-floor`} />}
        {showStore && <StatPair title="Store" icon={Storefront} accent data={block.store} testId={`staff-${title.toLowerCase().replace(/\s/g, "")}-store`} />}
      </div>
    </section>
  );
}

function IncentiveTable({ rows, mySalesman, testIdPrefix }) {
  const ranks = computeRanks(
    rows.map((s) => ({ name: s.salesman, abv: s.abv, abq: s.abq, total_incentive: s.total_incentive })),
    ["abv", "abq", "total_incentive"]
  );
  return (
    <div className="thin-scroll overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="w-full min-w-[640px]">
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50/60 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <th className="px-3 py-2.5">Salesman</th>
            <th className="px-3 py-2.5 text-right">ABV</th>
            <th className="px-3 py-2.5 text-right">ABQ</th>
            <th className="px-3 py-2.5 text-right">Incentive Till Day Before Yest.</th>
            <th className="px-3 py-2.5 text-right">Yesterday Incentive</th>
            <th className="px-3 py-2.5 text-right">Total Incentive</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.salesman} data-testid={`${testIdPrefix}-${s.salesman}`} className={`border-b border-slate-100 ${s.salesman === mySalesman ? "bg-blue-50" : ""}`}>
              <td className="px-3 py-3 text-sm font-medium text-slate-800">
                {s.salesman}{s.salesman === mySalesman && <span className="ml-1 text-xs font-semibold text-blue-600">(You)</span>}
                {s.target_achieved && <span className="ml-2 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">2×</span>}
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-right font-mono-data text-sm text-slate-700">{fmtCurrency(s.abv, true)}<RankBadge rank={ranks.abv?.[s.salesman]} /></td>
              <td className="whitespace-nowrap px-3 py-3 text-right font-mono-data text-sm text-slate-700">{fmtDecimal(s.abq)}<RankBadge rank={ranks.abq?.[s.salesman]} /></td>
              <td className="px-3 py-3 text-right font-mono-data text-sm text-slate-600">{fmtCurrency(s.incentive_till_day_before_yesterday, true)}</td>
              <td className="px-3 py-3 text-right font-mono-data text-sm text-slate-600">{fmtCurrency(s.yesterday_incentive, true)}</td>
              <td className="whitespace-nowrap px-3 py-3 text-right font-mono-data text-sm font-bold text-slate-900">{fmtCurrency(s.total_incentive, true)}<RankBadge rank={ranks.total_incentive?.[s.salesman]} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function StaffDashboard() {
  const { user } = useAuth();
  const canFloorTarget = scopeActive(user.floor_target_scope);
  const canIncentive = scopeActive(user.incentive_scope);
  const [data, setData] = useState(null);
  const [ft, setFt] = useState(null);
  const [inc, setInc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [segment, setSegment] = useState(false);

  useEffect(() => {
    const calls = [api.get("/staff/dashboard")];
    calls.push(canFloorTarget ? api.get("/staff/floor-targets") : Promise.resolve({ data: null }));
    calls.push(canIncentive ? api.get("/staff/incentive") : Promise.resolve({ data: null }));
    Promise.all(calls)
      .then(([d, f, i]) => { setData(d.data); setFt(f.data); setInc(i.data); })
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load your performance"))
      .finally(() => setLoading(false));
  }, [canFloorTarget, canIncentive]);

  if (loading) {
    return (
      <Layout title="My Dashboard">
        <div className="grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-32 animate-pulse rounded-lg border border-slate-200 bg-white" />)}
        </div>
      </Layout>
    );
  }
  if (err) {
    return <Layout title="My Dashboard"><EmptyState icon={User} title="Performance unavailable" message={err} testId="staff-error" /></Layout>;
  }

  const mySalesman = data.salesman_name;
  const salesmen = inc?.salesmen || [];
  const groups = {};
  for (const s of salesmen) { const key = s.core_floor || "Unassigned"; (groups[key] = groups[key] || []).push(s); }
  const groupKeys = Object.keys(groups).sort();

  const nothingToShow = !data.has_own && !data.show_store && !canFloorTarget && !canIncentive;

  return (
    <Layout title="My Dashboard">
      <div className="animate-fade-in space-y-6">
        <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-5">
          <div className="text-sm text-slate-500">Welcome back</div>
          <div className="font-display text-2xl font-extrabold tracking-tight text-slate-900">{data.salesman_name || user?.name || "Dashboard"}</div>
          {data.has_own && <div className="mt-1 flex items-center gap-1.5 text-sm text-slate-600"><Buildings size={16} weight="bold" /> {data.floor || "No floor assigned"}</div>}
        </div>

        {nothingToShow && (
          <EmptyState icon={Storefront} title="No dashboard access yet" message="Ask your administrator to enable dashboard sections for your account." testId="staff-no-access" />
        )}

        {(data.has_own || data.show_store) && (
          <>
            <Section title="Yesterday" icon={CalendarCheck} block={data.yesterday} hasOwn={data.has_own} showStore={data.show_store} />
            <Section title="This Month" icon={CalendarBlank} sub="(through yesterday)" block={data.month} hasOwn={data.has_own} showStore={data.show_store} />
          </>
        )}

        {canFloorTarget && ft && ft.floor_targets.length > 0 && (
          <section data-testid="staff-floortarget-section">
            <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-slate-800"><Target size={20} weight="bold" className="text-blue-700" /> Floor Target Achievement</h2>
            <FloorTargetBoard mode="staff" data={ft} />
          </section>
        )}

        {canIncentive && inc && (
          <section data-testid="staff-incentive-section">
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-800"><Trophy size={20} weight="fill" className="text-amber-500" /> Incentive Competition</h2>
              <button onClick={() => setSegment((v) => !v)} data-testid="segment-toggle"
                className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${segment ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}>
                <span className={`inline-block h-2 w-2 rounded-full ${segment ? "bg-white" : "bg-slate-400"}`} />
                Segment by Floor: {segment ? "On" : "Off"}
              </button>
            </div>
            <p className="mb-3 text-xs text-slate-400">This month through yesterday · base rate {inc.base_pct}% (doubles when your core floor hits target)</p>
            {!segment ? (
              <IncentiveTable rows={salesmen} mySalesman={mySalesman} testIdPrefix="incentive-row" />
            ) : (
              <div className="space-y-4" data-testid="incentive-segmented">
                {groupKeys.map((g) => (
                  <div key={g} data-testid={`incentive-group-${g}`}>
                    <div className="mb-1.5 flex items-center gap-1.5 text-sm font-bold text-slate-700"><Buildings size={15} weight="bold" className="text-blue-700" /> {g}</div>
                    <IncentiveTable rows={groups[g]} mySalesman={mySalesman} testIdPrefix={`incentive-row-${g}`} />
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </Layout>
  );
}
